import ResizableDrawer, { Anchor } from "@components/ResizableDrawer/ResizableDrawer"
import NodeForm from "@features/builder/NodeDrawer/NodeForm"
import { useNodes, useReactFlow } from "@xyflow/react"
import { useEffect, useState } from "react"
import { AppNode, NodeType } from "@features/builder/graph/types"
import {
  doesNodeNeedTypeUpdate,
  getChildrenNodes,
  updateNodeType,
} from "@features/builder/helpers/nodes"
import { useLocalStorage } from "usehooks-ts"
import { useConfirmPageLeave } from "@hooks/useConfirmPageLeave"
import ConfirmDialog from "@components/ConfirmDialog"
import { Grid, Typography } from "@mui/material"
import {
  isClosingNodeDrawerInvokedAtom,
  nextDrawerNodeIdAtom,
  nodeFormHasUnsavedChangesAtom,
} from "@features/builder/graph/graphAtoms"
import NodeDrawerOptionsMenu, {
  CLOSE_NODE_DRAWER_ON_MULTISELECT_LS_KEY,
  CLOSE_NODE_DRAWER_ON_SAVE_LS_KEY,
} from "@features/builder/NodeDrawer/NodeDrawerOptionsMenu"
import { enqueueSnackbar } from "notistack"
import { updateParentParamsOfChildNode } from "@features/builder/helpers/update-children-node"
import { isEqual } from "lodash"
import {
  isMyLock,
  socketLockAcquire,
  socketLockRelease,
} from "@features/builder/hooks/socketIOHooks"
import useNodeLock from "@features/builder/hooks/useNodeLock"
import { ActionButtons } from "@features/builder/NodeDrawer/NodeForm/ActionButtons"
import NodeFormPreview from "@features/builder/NodeDrawer/NodeForm/NodeFormPreview/NodeFormPreview"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { TKNode } from "@store/services/converter/types"
import { useGetLinkedNode } from "@features/builder/hooks/useGetLinkedNode"
import { useAtom } from "jotai"
import { getLockRelatedNodes } from "@features/builder/NodeDrawer/lockRelatedNodes"

const collaborativeLockDepth = Number(
  import.meta.env.VITE_FF_BUILDER_COLLABORATIVE_LOCK_DEPTH?.trim() ?? 2,
)

type NodeDrawerProps = {
  graphId: string | undefined
  disabled: boolean
}

const NodeDrawer = (props: NodeDrawerProps) => {
  const [isFormDirty, setIsFormDirty] = useAtom(nodeFormHasUnsavedChangesAtom)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [linkedNodeId, setLinkedNodeId] = useState<string | null>(null)
  const [anchor, setAnchor] = useLocalStorage<Anchor>("graph-builder-node-drawer-anchor", "right")
  const [nextDrawerNodeId, setNextDrawerNodeId] = useAtom(nextDrawerNodeIdAtom)
  const [isClosingNodeDrawerInvoked, setIsClosingNodeDrawerInvoked] = useAtom(
    isClosingNodeDrawerInvokedAtom,
  )
  const { updateNode, getNodes, getNode } = useReactFlow<AppNode>()
  const nodes = useNodes<AppNode>()

  const { getLinkedNode } = useGetLinkedNode()

  const [closeDrawerOnSave] = useLocalStorage(CLOSE_NODE_DRAWER_ON_SAVE_LS_KEY, false)
  const [closeOnMultiselect] = useLocalStorage(CLOSE_NODE_DRAWER_ON_MULTISELECT_LS_KEY, true)

  const navigationBlocker = useConfirmPageLeave(isFormDirty, {
    onPageLeave: () => setIsConfirmDialogOpen(true),
    shouldBlockNavigation: (currentLocation, nextLocation) => {
      // there is no selected node - do not block navigation
      if (!selectedNodeId) return false

      // going to completely different page needs confirmation
      if (currentLocation.pathname !== nextLocation.pathname) return true

      const currentSearchParams = new URLSearchParams(currentLocation.search)
      const nextSearchParams = new URLSearchParams(nextLocation.search)

      // changing graph needs to be confirmed
      if (currentSearchParams.get("graphKey") !== nextSearchParams.get("graphKey")) return true

      // other search params does not require confirmation
      return false
    },
  })

  // get selected node directly from useNodes() so we are sure we gonna get the most recent version of that node
  const selectedNode: AppNode | null = selectedNodeId
    ? nodes.find(({ id }) => id === selectedNodeId) ?? null
    : null

  const nodeLock = useNodeLock({ nodeId: selectedNodeId ?? "" })

  // reset nextDrawerNodeId when it was already opened with that node
  useEffect(() => {
    setNextDrawerNodeId(null)
    setIsFormDirty(false)
  }, [selectedNodeId, setNextDrawerNodeId, setIsFormDirty])

  // on trying to change node / close drawer, show confirmation dialog or change selected node
  useEffect(() => {
    if (!nextDrawerNodeId) return

    if (isFormDirty) setIsConfirmDialogOpen(true)
    else setSelectedNodeId(nextDrawerNodeId)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextDrawerNodeId])

  // on trying to close node drawer from outside, make sure there are no unsaved changes
  useEffect(() => {
    if (!isClosingNodeDrawerInvoked) return

    if (isFormDirty) setIsConfirmDialogOpen(true)
    else setSelectedNodeId(null)

    setIsClosingNodeDrawerInvoked(false)
  }, [isClosingNodeDrawerInvoked, isFormDirty, setIsClosingNodeDrawerInvoked, setIsFormDirty])

  // clear selection on graph change
  useEffect(() => {
    setSelectedNodeId(null)
    setIsFormDirty(false)
  }, [props.graphId, setIsFormDirty])

  if (!selectedNode) return <></>

  const handleClose =
    selectedNode.type !== NodeType.Empty
      ? () => {
          if (isFormDirty) setIsConfirmDialogOpen(true)
          else setSelectedNodeId(null)
        }
      : undefined

  const handleSubmit = async (updatedNode: AppNode) => {
    const allNodes = getNodes()

    const previousVersionOfUpdatedNode = allNodes.find((node) => node.id === updatedNode.id)
    if (!previousVersionOfUpdatedNode) return

    const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, node]))
    let updatedNodeCopy = structuredClone(updatedNode)
    allNodesUpdatedMap.set(updatedNodeCopy.id, updatedNodeCopy)

    if (doesNodeNeedTypeUpdate(updatedNodeCopy, allNodes)) {
      updatedNodeCopy = updateNodeType(updatedNodeCopy, allNodes)
      allNodesUpdatedMap.set(updatedNodeCopy.id, updatedNodeCopy)
    }

    const updatedChildrenNodes: AppNode[] = getChildrenNodes(
      updatedNodeCopy,
      Array.from(allNodesUpdatedMap.values()),
    )

    // changing parent node type may cause conversion children mixture <-> continous types
    updatedChildrenNodes.forEach((childNode) => {
      if (!doesNodeNeedTypeUpdate(childNode, Array.from(allNodesUpdatedMap.values()))) return

      const updatedNode = updateNodeType(childNode, Array.from(allNodesUpdatedMap.values()))
      allNodesUpdatedMap.set(updatedNode.id, updatedNode)
    })

    const previousLink =
      "metaNode" in previousVersionOfUpdatedNode.data
        ? previousVersionOfUpdatedNode.data.metaNode.Link
        : ""
    const updatedLink = "metaNode" in updatedNodeCopy.data ? updatedNodeCopy.data.metaNode.Link : ""
    let linkedNode: TKNode | undefined = undefined

    if (updatedLink && updatedLink !== previousLink) {
      linkedNode = await getLinkedNode(updatedLink)
    }

    updatedChildrenNodes.forEach((childNode) => {
      // updating child node may cause updating also it's child, that may cause update of grandchild etc.
      const updatedNodes = updateParentParamsOfChildNode(
        childNode,
        previousVersionOfUpdatedNode,
        updatedNode,
        Array.from(allNodesUpdatedMap.values()),
        linkedNode,
      )
      updatedNodes.forEach((node) => allNodesUpdatedMap.set(node.id, node))
    })

    for (const node of allNodes) {
      const updatedNode = allNodesUpdatedMap.get(node.id)
      const hasChanged = !isEqual(node, updatedNode)

      if (updatedNode && hasChanged) updateNode(node.id, updatedNode, { replace: true })
    }

    if (closeDrawerOnSave) setSelectedNodeId(null)

    if (isMyLock(nodeLock)) socketLockRelease(nodeLock?.lock)

    enqueueSnackbar("Changes saved", { variant: "success", preventDuplicate: true })
  }

  const handleConfirmDialogClose = (isConfirmed: boolean) => {
    setIsConfirmDialogOpen(false)

    if (isMyLock(nodeLock)) socketLockRelease(nodeLock?.lock)

    if (isConfirmed) {
      const selectedNodes = getNodes().filter((node) => node.selected)

      if (closeOnMultiselect && selectedNodes.length > 1) {
        setSelectedNodeId(null)
      } else {
        setSelectedNodeId(nextDrawerNodeId)
      }

      // user moves away from the page
      if (navigationBlocker.state === "blocked") {
        navigationBlocker.proceed()
      }
    } else {
      setNextDrawerNodeId(null)
    }
  }

  const handleDirtyChange = (isDirty: boolean) => {
    setIsFormDirty(isDirty)
    if (!selectedNode || !props.graphId) return

    // lock nodes if changes are made and it is not yet locked
    if (!nodeLock && isDirty)
      socketLockAcquire(
        props.graphId,
        selectedNode.id,
        getLockRelatedNodes({
          allNodes: getNodes(),
          currNodeId: selectedNode.id,
          depth: collaborativeLockDepth,
        }),
      )

    // unlock if there are no longer any changes and there is still a lock
    if (nodeLock && !isDirty) socketLockRelease(nodeLock.lock)
  }

  return (
    <>
      <ResizableDrawer
        SlideProps={{ appear: false }}
        open
        hideBackdrop
        disableEnforceFocus
        showBackdrop={selectedNode?.type === NodeType.Empty}
        anchor={anchor}
        onAnchorChange={setAnchor}
        localStoragePrefix="builder-page"
        // Do not scroll the Errors Drawer back to the selected error when this drawer closes.
        ModalProps={{ disableRestoreFocus: true }}
        onClose={handleClose}
        menuComponent={<NodeDrawerOptionsMenu />}
        PaperProps={{
          ...(anchor === "right" ? { sx: { top: "40px" } } : {}),
        }}
      >
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          wrap="nowrap"
          gap={0}
          sx={{ height: "100%", p: 0 }}
        >
          <Grid
            container
            item
            xs
            direction="column"
            wrap="nowrap"
            sx={{ overflow: "scroll", pr: "15px" }}
          >
            <Grid item>
              <NodeForm
                key={selectedNode.id}
                node={selectedNode}
                disabled={props.disabled}
                onSubmit={handleSubmit}
                onDirtyChange={handleDirtyChange}
                onLinkedNodeChange={(id) => setLinkedNodeId(id)}
              />
            </Grid>
            {linkedNodeId && (
              <Grid
                item
                sx={{ border: "2px solid rgba(255, 165, 0, .6)", borderRadius: "1%", p: 1 }}
              >
                <NodeDrawerAccordion id="linked-node-preview" label="Linked node preview">
                  <Grid item>
                    <NodeFormPreview linkedNodeId={linkedNodeId} />
                  </Grid>
                </NodeDrawerAccordion>
              </Grid>
            )}
          </Grid>

          {/* bottom action bar */}
          <Grid item xs="auto" sx={{ mb: "15px" }}>
            <ActionButtons
              isSaveDisabled={props.disabled || !isFormDirty}
              node={selectedNode}
              nodeLock={nodeLock}
            />
          </Grid>
        </Grid>
      </ResizableDrawer>

      <ConfirmDialog
        open={isConfirmDialogOpen}
        onClose={handleConfirmDialogClose}
        dialogTitle="Do you want to discard changes?"
      >
        <Typography>
          Your changes are not saved. <br />
          Are you sure you want to close node drawer without saving your changes?
        </Typography>
      </ConfirmDialog>
    </>
  )
}

export default NodeDrawer
